import React, { useState } from 'react';
import { t } from '../lib/i18n';
import { X, Mail, Lock, User, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatApiError, api } from '../lib/api';

const inputClass = 'w-full pl-10 pr-4 py-3 rounded-sm bg-[#262626] border-none text-white text-sm placeholder-[#808080] focus:outline-none focus:ring-2 focus:ring-[#E50914]';

const Field = ({ icon: Icon, label, testId, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-[#B3B3B3] mb-1.5">{label}</label>
    <div className="relative">
      <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#808080]" />
      <input data-testid={testId} className={inputClass} {...props} />
    </div>
  </div>
);

const AuthModal = () => {
  const { authModalOpen, closeAuth, login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);

  if (!authModalOpen) return null;

  const switchMode = (m) => { setMode(m); setError(''); setResetInfo(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'forgot') {
        const { data } = await api.post('/auth/forgot-password', { email, origin: window.location.origin });
        setResetInfo(data);
        return;
      }
      if (mode === 'login') await login(email, password);
      else await register(name, email, password);
      setEmail(''); setPassword(''); setName('');
      closeAuth();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const titles = { login: t('Se connecter'), register: t('Créer un compte'), forgot: t('Mot de passe oublié') };
  const subtitles = { login: t('Connectez-vous pour gérer vos favoris et centres d’intérêt'), register: t('Créez un compte pour personnaliser votre expérience'), forgot: t('Saisissez votre email et nous vous enverrons un lien de réinitialisation') };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeAuth} />
      <div className="relative bg-[#141414] border border-[#262626] rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-8 pt-8 pb-2">
          <button onClick={closeAuth} data-testid="auth-modal-close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-white">
            <X size={16} />
          </button>
          <h2 className="text-2xl font-black text-white" data-testid="auth-modal-title">{titles[mode]}</h2>
          <p className="text-sm text-[#808080] mt-1">{subtitles[mode]}</p>
        </div>

        {resetInfo ? (
          <div className="p-8 pt-6" data-testid="forgot-password-sent">
            <p className="text-sm text-[#B3B3B3] bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-2.5">{t('Si un compte existe pour cet email, un lien de réinitialisation a été envoyé.')}</p>
            {resetInfo.reset_link && (
              <div className="mt-4 text-xs text-[#808080] bg-[#262626] rounded-sm px-3 py-2.5" data-testid="forgot-password-mock-link">
                <p className="font-bold uppercase tracking-wider text-amber-400 mb-1">Mode démo — email non envoyé</p>
                <a href={resetInfo.reset_link} className="text-white underline break-all hover:text-[#E50914]">{resetInfo.reset_link}</a>
              </div>
            )}
            <button type="button" onClick={() => switchMode('login')} className="mt-5 w-full text-sm text-[#808080] hover:text-white font-medium" data-testid="auth-back-to-login">{t('Retour à la connexion')}</button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-8 pt-6">
          {mode === 'register' && (
            <Field icon={User} label={t('Nom')} testId="auth-name-input" type="text" value={name} required
              onChange={(e) => setName(e.target.value)} placeholder={t('Votre nom')} />
          )}
          <Field icon={Mail} label="Email" testId="auth-email-input" type="email" value={email} required
            onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.fr" />
          {mode !== 'forgot' && (
            <Field icon={Lock} label={t('Mot de passe')} testId="auth-password-input" type="password" value={password} required minLength={6}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          )}
          {mode === 'login' && (
            <div className="-mt-2 mb-4 text-right">
              <button type="button" onClick={() => switchMode('forgot')} className="text-xs text-[#808080] hover:text-white font-medium" data-testid="auth-forgot-password">{t('Mot de passe oublié ?')}</button>
            </div>
          )}

          {error && (
            <p className="mb-4 -mt-1 text-sm text-[#E50914] bg-[#E50914]/10 border border-[#E50914]/30 rounded-sm px-3 py-2" data-testid="auth-error">
              {error}
            </p>
          )}

          <button type="submit" disabled={loading} data-testid="auth-modal-submit"
            className="w-full py-3 rounded-sm bg-[#E50914] text-white font-bold text-sm hover:bg-[#F40612] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {mode === 'login' ? t('Se connecter') : mode === 'register' ? t('Créer un compte') : t('Envoyer le lien')}
          </button>

          <div className="text-center mt-5">
            {mode === 'forgot' ? (
              <button type="button" onClick={() => switchMode('login')} className="text-sm text-[#808080] hover:text-white font-medium transition-colors" data-testid="auth-back-to-login">{t('Retour à la connexion')}</button>
            ) : (
              <button type="button" onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
                className="text-sm text-[#808080] hover:text-white font-medium transition-colors" data-testid="auth-toggle-mode">
                {mode === 'login' ? t('Pas encore de compte ? Créez-en un') : t('Vous avez déjà un compte ? Connectez-vous')}
              </button>
            )}
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
